/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Chip,
  Box,
  Button,
  CircularProgress,
} from '@mui/material'
import { getDocs, getRegs } from '../../../utils/fc'
import oikosApi from '../../../services/oikos'
import { notifyError, notifySuccess } from '../../../utils/notify'
import { uploadFileToDocumento } from '../../../utils/oikosUploadDocumento'
import { FAT_SUPPORT_DOC_TYPES } from '../../../constants/oikosReceitas'
import { invalidateFatSupportCache } from '../../../utils/fatSupportDocs'
import SeriesFaturamentoEditor from './SeriesFaturamentoEditor'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const ECOSSIS_PJ_IDS = [1, 2]
const FAT_DOC_TAB_REF = 'projFat'

const emptyForm = () => ({
  proj_fatProc: '',
  proj_fatRespStake_id: null,
  proj_fatDiaLim: '',
  proj_nfDescTpl: '',
  retTemplate_id: null,
  proj_cno: '',
  proj_fatEmailAssunto: '',
  proj_fatEmailTpl: '',
  emails: [],
  emailDraft: '',
  contrato_prazoPgto: '',
  contrato_codTrib: '',
  contrato_munISS: null,
  recebeIds: [],
  fatDocIds: [],
})

const asText = (v) => (v == null ? '' : String(v))

const parseEmails = (raw) => {
  if (Array.isArray(raw)) return raw.map((x) => String(x))
  if (raw == null || raw === '') return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return Array.isArray(parsed) ? parsed.map((x) => String(x)) : []
  } catch (e) {
    return []
  }
}

const nullIfBlank = (v) => {
  const s = String(v ?? '').trim()
  return s === '' ? null : s
}

const asDocs = (x) => (Array.isArray(x) ? x : [])

const docOption = (d, types, group) => {
  const id = Number(d?.documento_id)
  if (!(id > 0)) return null
  const tp = types.find((t) => Number(t.tpDocumento_id) === Number(d.tpDocumento_id))
  const tipo = tp?.tpDocumento_desc || (d.tpDocumento_id ? `Tipo ${d.tpDocumento_id}` : 'Documento')
  const desc = String(d.documento_desc || '').trim()
  return {
    documento_id: id,
    tpDocumento_id: d.tpDocumento_id != null ? Number(d.tpDocumento_id) : null,
    group,
    label: desc ? `${tipo} — ${desc}` : tipo,
  }
}

const mergeDocOptions = (lists, types, group) => {
  const byId = new Map()
  lists.flatMap(asDocs).forEach((d) => {
    const opt = docOption(d, types, group)
    if (opt && !byId.has(opt.documento_id)) byId.set(opt.documento_id, opt)
  })
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
}

const stakeLabel = (s) => {
  const nome = s?.pf_nomeExibicao || `Stakeholder ${s?.projStake_id}`
  const papel = s?.papel_nome ? ` — ${s.papel_nome}` : ''
  const email = s?.pf_emailProf ? ` — ${s.pf_emailProf}` : ''
  return `${nome}${papel}${email}`
}

export default function ModalProcessoFaturamento({ open, onClose, projId, contratoId }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [stakes, setStakes] = useState([])
  const [muns, setMuns] = useState([])
  const [templates, setTemplates] = useState([])
  const [contratanteId, setContratanteId] = useState(null)
  const [origRecebe, setOrigRecebe] = useState({})
  const [instDocs, setInstDocs] = useState([])
  const [projDocs, setProjDocs] = useState([])
  const [origFatVincs, setOrigFatVincs] = useState({})
  const [uploadTp, setUploadTp] = useState(null)
  const [uploadDesc, setUploadDesc] = useState('')
  const [uploadFimVig, setUploadFimVig] = useState('')
  const [uploadDtEmissao, setUploadDtEmissao] = useState('')
  const [series, setSeries] = useState([])
  const [origSeriesIds, setOrigSeriesIds] = useState([])
  const [recs, setRecs] = useState([])
  const [origRecTpl, setOrigRecTpl] = useState({})
  const fileRef = useRef(null)

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  useEffect(() => {
    if (!open || !projId) return undefined
    let cancel = false
    const load = async () => {
      setLoading(true)
      try {
        const stakeFields = {
          '(SELECT pf_nomeExibicao FROM pf WHERE pf.pf_id = projStake.pf_id)': 'pf_nomeExibicao',
          '(SELECT pf_emailProf FROM pf WHERE pf.pf_id = projStake.pf_id)': 'pf_emailProf',
          '(SELECT pj_id FROM pf WHERE pf.pf_id = projStake.pf_id)': 'pj_id',
          '(SELECT papel_nome FROM papel WHERE papel.papel_id = projStake.papel_id)': 'papel_nome',
        }
        const [projRows, contratoRows, stakeRows, munRows, tplRows, pjDocsA, pjDocsB, projFileDocs, fatDocs, tpRows, fatTplRows] = await Promise.all([
          getRegs('proj', { proj_id: projId }),
          contratoId ? getRegs('contrato', { contrato_id: contratoId }) : Promise.resolve([]),
          getRegs('projStake', { proj_id: projId }, stakeFields),
          getRegs('mun', {}, { mun_id: 'mun_id', mun_nome: 'mun_nome', uf_IBGECod: 'uf_IBGECod' }),
          getRegs('retTemplate', {}, {
            retTemplate_id: 'retTemplate_id',
            retTemplate_desc: 'retTemplate_desc',
            kamino_id: 'kamino_id',
            retTemplate_ativo: 'retTemplate_ativo',
          }, false),
          getDocs('pj', ECOSSIS_PJ_IDS[0]),
          getDocs('pj', ECOSSIS_PJ_IDS[1]),
          getDocs('proj', projId),
          getDocs(FAT_DOC_TAB_REF, projId),
          getRegs('tpDocumento', {}, { tpDocumento_id: 'tpDocumento_id', tpDocumento_desc: 'tpDocumento_desc' }),
          getRegs('projFatTpl', { proj_id: projId, projFatTpl_ativo: 1 }),
        ])
        if (cancel) return
        const proj = Array.isArray(projRows) ? projRows[0] : null
        const contrato = Array.isArray(contratoRows) ? contratoRows[0] : null
        const stakeList = Array.isArray(stakeRows) ? stakeRows : []
        const recebe = {}
        const recebeIds = []
        stakeList.forEach((s) => {
          const on = Number(s.projStake_recebeNf) === 1
          recebe[s.projStake_id] = on
          if (on) recebeIds.push(Number(s.projStake_id))
        })
        setStakes(stakeList)
        setMuns(Array.isArray(munRows) ? munRows : [])
        setTemplates(Array.isArray(tplRows) ? tplRows : [])
        setContratanteId(contrato?.contrato_contratante ?? null)
        setOrigRecebe(recebe)
        const types = Array.isArray(tpRows) ? tpRows : []
        const inst = mergeDocOptions([pjDocsA, pjDocsB], types, 'Ecossis')
        const fromProj = mergeDocOptions([projFileDocs, fatDocs], types, 'Este projeto')
        const selected = asDocs(fatDocs)
        const origVincs = {}
        const fatDocIds = []
        selected.forEach((d) => {
          const id = Number(d.documento_id)
          if (!(id > 0)) return
          fatDocIds.push(id)
          if (d.documentoVinc_id) origVincs[id] = Number(d.documentoVinc_id)
        })
        setInstDocs(inst)
        setProjDocs(fromProj)
        setOrigFatVincs(origVincs)
        setUploadTp(null)
        setUploadDesc('')
        setUploadFimVig('')
        setUploadDtEmissao('')
        const serieList = (Array.isArray(fatTplRows) ? fatTplRows : [])
          .slice()
          .sort((a, b) => Number(a.projFatTpl_ord || 0) - Number(b.projFatTpl_ord || 0))
          .map((s) => ({
            projFatTpl_id: Number(s.projFatTpl_id) || null,
            projFatTpl_nome: asText(s.projFatTpl_nome),
            projFatTpl_nfDescTpl: asText(s.projFatTpl_nfDescTpl),
            projFatTpl_epPrefix: asText(s.projFatTpl_epPrefix),
            projFatTpl_cnpjTom: asText(s.projFatTpl_cnpjTom),
            mun_id: s.mun_id != null && s.mun_id !== '' ? Number(s.mun_id) : null,
            projFatTpl_padrao: Number(s.projFatTpl_padrao) === 1 ? 1 : 0,
          }))
        let recList = []
        if (proj?.orcRubr_id) {
          recList = await getRegs('rec', { orcRubr_id: proj.orcRubr_id }) || []
        }
        if (cancel) return
        const byTpl = {}
        const origTpl = {}
        ;(Array.isArray(recList) ? recList : []).forEach((r) => {
          const rid = Number(r.rec_id)
          const tid = Number(r.projFatTpl_id) || 0
          origTpl[rid] = tid || null
          if (tid > 0) {
            if (!byTpl[tid]) byTpl[tid] = []
            byTpl[tid].push(rid)
          }
        })
        serieList.forEach((s) => {
          s.recIds = byTpl[s.projFatTpl_id] || []
        })
        setSeries(serieList)
        setOrigSeriesIds(serieList.map((s) => s.projFatTpl_id).filter(Boolean))
        setRecs(Array.isArray(recList) ? recList : [])
        setOrigRecTpl(origTpl)
        setForm({
          proj_fatProc: asText(proj?.proj_fatProc),
          proj_fatRespStake_id: proj?.proj_fatRespStake_id != null ? Number(proj.proj_fatRespStake_id) : null,
          proj_fatDiaLim: proj?.proj_fatDiaLim != null && proj.proj_fatDiaLim !== '' ? String(proj.proj_fatDiaLim) : '',
          proj_nfDescTpl: asText(proj?.proj_nfDescTpl),
          retTemplate_id: proj?.retTemplate_id != null && proj.retTemplate_id !== ''
            ? Number(proj.retTemplate_id)
            : null,
          proj_cno: asText(proj?.proj_cno),
          proj_fatEmailAssunto: asText(proj?.proj_fatEmailAssunto),
          proj_fatEmailTpl: asText(proj?.proj_fatEmailTpl),
          emails: parseEmails(proj?.proj_fatEmailExtra),
          emailDraft: '',
          contrato_prazoPgto: contrato?.contrato_prazoPgto != null && contrato.contrato_prazoPgto !== ''
            ? String(contrato.contrato_prazoPgto)
            : '',
          contrato_codTrib: asText(contrato?.contrato_codTrib),
          contrato_munISS: contrato?.contrato_munISS != null ? Number(contrato.contrato_munISS) : null,
          recebeIds,
          fatDocIds,
        })
      } catch (e) {
        console.log('ModalProcessoFaturamento load', e)
        notifyError('Não foi possível carregar o processo de faturamento.')
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    load()
    return () => { cancel = true }
  }, [open, projId, contratoId])

  const respOptions = useMemo(() => {
    const client = contratanteId == null
      ? stakes
      : stakes.filter((s) => Number(s.pj_id) === Number(contratanteId))
    const base = client.length ? client : stakes
    const selectedId = form.proj_fatRespStake_id
    if (selectedId != null && !base.some((s) => Number(s.projStake_id) === Number(selectedId))) {
      const extra = stakes.find((s) => Number(s.projStake_id) === Number(selectedId))
      if (extra) return [extra, ...base]
    }
    return base
  }, [stakes, contratanteId, form.proj_fatRespStake_id])

  const respValue = respOptions.find((s) => Number(s.projStake_id) === Number(form.proj_fatRespStake_id))
    || stakes.find((s) => Number(s.projStake_id) === Number(form.proj_fatRespStake_id))
    || null

  const munValue = muns.find((m) => Number(m.mun_id) === Number(form.contrato_munISS)) || null

  const tplOptions = templates.filter((t) => Number(t.retTemplate_ativo) === 1
    || Number(t.retTemplate_id) === Number(form.retTemplate_id))
  const tplValue = templates.find((t) => Number(t.retTemplate_id) === Number(form.retTemplate_id)) || null

  const docOptions = useMemo(() => {
    const byId = new Map()
    instDocs.forEach((d) => byId.set(d.documento_id, { ...d, group: 'Ecossis' }))
    projDocs.forEach((d) => {
      if (!byId.has(d.documento_id)) byId.set(d.documento_id, { ...d, group: 'Este projeto' })
    })
    return Array.from(byId.values()).sort((a, b) => {
      if (a.group !== b.group) return a.group.localeCompare(b.group, 'pt-BR')
      return a.label.localeCompare(b.label, 'pt-BR')
    })
  }, [instDocs, projDocs])

  const selectedDocs = useMemo(
    () => docOptions.filter((d) => form.fatDocIds.includes(Number(d.documento_id))),
    [docOptions, form.fatDocIds],
  )

  const selectedTypeIds = useMemo(() => {
    const ids = new Set()
    selectedDocs.forEach((d) => {
      if (d.tpDocumento_id != null) ids.add(Number(d.tpDocumento_id))
    })
    return ids
  }, [selectedDocs])

  const addEmail = () => {
    const v = String(form.emailDraft || '').trim().toLowerCase()
    if (!EMAIL_RE.test(v) || v.includes("'") || v.includes('"')) {
      notifyError('Informe um e-mail válido.')
      return
    }
    if (form.emails.includes(v)) {
      setField('emailDraft', '')
      return
    }
    setForm((f) => ({ ...f, emails: [...f.emails, v], emailDraft: '' }))
  }

  const toggleId = (key, id) => {
    setForm((f) => {
      const cur = f[key]
      const has = cur.includes(id)
      return { ...f, [key]: has ? cur.filter((x) => x !== id) : [...cur, id] }
    })
  }

  const onPickFile = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file || !projId) return
    if (!uploadTp) {
      notifyError('Escolha o tipo do documento antes de anexar.')
      return
    }
    if (!uploadFimVig) {
      notifyError('Informe até quando o documento é válido.')
      return
    }
    setUploading(true)
    try {
      const desc = String(uploadDesc || '').trim() || file.name
      const documentoId = await uploadFileToDocumento({
        tpDocumentoId: uploadTp.id,
        tabRef: 'proj',
        tabId: projId,
        file,
        documentoDesc: desc,
        documentoDtEmissao: uploadDtEmissao || undefined,
        documentoIniVig: uploadDtEmissao || undefined,
        documentoFimVig: uploadFimVig,
      })
      const opt = {
        documento_id: Number(documentoId),
        tpDocumento_id: Number(uploadTp.id),
        group: 'Este projeto',
        label: `${uploadTp.label} — ${desc}`,
      }
      setProjDocs((cur) => (cur.some((d) => Number(d.documento_id) === opt.documento_id) ? cur : [...cur, opt]))
      setForm((f) => (
        f.fatDocIds.includes(opt.documento_id)
          ? f
          : { ...f, fatDocIds: [...f.fatDocIds, opt.documento_id] }
      ))
      setUploadDesc('')
      setUploadFimVig('')
      setUploadDtEmissao('')
      notifySuccess('Arquivo anexado ao projeto e incluído no faturamento.')
    } catch (err) {
      console.log('ModalProcessoFaturamento upload', err?.response?.data || err)
      notifyError('Não foi possível anexar o arquivo.')
    } finally {
      setUploading(false)
    }
  }

  const save = async () => {
    if (!projId) return
    const diaRaw = String(form.proj_fatDiaLim || '').trim()
    let dia = null
    if (diaRaw !== '') {
      dia = Number(diaRaw)
      if (!Number.isInteger(dia) || dia < 1 || dia > 31) {
        notifyError('O dia limite precisa ser um número de 1 a 31.')
        return
      }
    }
    const cno = String(form.proj_cno || '').replace(/\D/g, '')
    if (cno.length > 12) {
      notifyError('O CNO tem no máximo 12 dígitos.')
      return
    }
    let prazo = null
    if (contratoId) {
      const prazoRaw = String(form.contrato_prazoPgto || '').trim()
      if (prazoRaw !== '') {
        prazo = Number(prazoRaw)
        if (!Number.isInteger(prazo) || prazo < 0 || prazo > 3650) {
          notifyError('O prazo de pagamento precisa ser um número de dias entre 0 e 3650.')
          return
        }
      }
    }
    setSaving(true)
    try {
      const projRes = await oikosApi.post('/', {
        action: 'update',
        table: 'proj',
        data: {
          proj_id: projId,
          proj_fatProc: nullIfBlank(form.proj_fatProc),
          proj_fatRespStake_id: form.proj_fatRespStake_id || null,
          proj_fatDiaLim: dia,
          proj_nfDescTpl: nullIfBlank(form.proj_nfDescTpl),
          retTemplate_id: form.retTemplate_id || null,
          proj_cno: cno || null,
          proj_fatEmailAssunto: nullIfBlank(form.proj_fatEmailAssunto),
          proj_fatEmailTpl: nullIfBlank(form.proj_fatEmailTpl),
          proj_fatEmailExtra: JSON.stringify(form.emails),
        },
      })
      if (projRes?.status && projRes.status >= 400) throw new Error('proj')

      if (contratoId) {
        const contratoRes = await oikosApi.post('/', {
          action: 'update',
          table: 'contrato',
          data: {
            contrato_id: contratoId,
            contrato_prazoPgto: prazo,
            contrato_codTrib: nullIfBlank(form.contrato_codTrib),
            contrato_munISS: form.contrato_munISS || null,
          },
        })
        if (contratoRes?.status && contratoRes.status >= 400) throw new Error('contrato')
      }

      const wanted = new Set(form.recebeIds.map(Number))
      for (const s of stakes) {
        const id = Number(s.projStake_id)
        const next = wanted.has(id)
        const prev = !!origRecebe[s.projStake_id] || !!origRecebe[id]
        if (next === prev) continue
        const stakeRes = await oikosApi.post('/', {
          action: 'update',
          table: 'projStake',
          data: {
            projStake_id: id,
            projStake_recebeNf: next ? 1 : 0,
          },
        })
        if (stakeRes?.status && stakeRes.status >= 400) throw new Error('stake')
      }

      const wantedDocs = new Set(form.fatDocIds.map(Number).filter((id) => id > 0))
      const origDocIds = new Set(Object.keys(origFatVincs).map(Number))
      for (const id of wantedDocs) {
        if (origDocIds.has(id)) continue
        const vincRes = await oikosApi.post('/', {
          action: 'insert',
          table: 'documentoVinc',
          data: {
            documento_id: id,
            documentoVinc_tabRef: FAT_DOC_TAB_REF,
            documentoVinc_tabId: projId,
          },
        })
        if (vincRes?.status && vincRes.status >= 400) throw new Error('fatDoc')
      }
      for (const id of origDocIds) {
        if (wantedDocs.has(id)) continue
        const vincId = origFatVincs[id] || origFatVincs[String(id)]
        if (!vincId) continue
        const delRes = await oikosApi.post('/', {
          action: 'delete',
          table: 'documentoVinc',
          data: { documentoVinc_id: vincId },
        })
        if (delRes?.status && delRes.status >= 400) throw new Error('fatDoc')
      }

      const toSave = series.filter((s) => s.mun_id || String(s.projFatTpl_nome || '').trim() || (s.recIds || []).length)
      for (const s of toSave) {
        if (!s.mun_id) {
          notifyError('Cada série precisa do município de prestação do serviço.')
          setSaving(false)
          return
        }
      }
      const kept = new Set()
      const idByIdx = []
      for (let i = 0; i < series.length; i += 1) {
        const s = series[i]
        if (!s.mun_id && !String(s.projFatTpl_nome || '').trim() && !(s.recIds || []).length) {
          idByIdx[i] = null
          continue
        }
        const munNome = (muns.find((m) => Number(m.mun_id) === Number(s.mun_id)) || {}).mun_nome || ''
        const nome = String(s.projFatTpl_nome || '').trim() || munNome
        if (!nome) {
          idByIdx[i] = null
          continue
        }
        const epPrefix = String(s.projFatTpl_epPrefix || '').trim() || munNome
        const payload = {
          proj_id: projId,
          projFatTpl_nome: nome,
          projFatTpl_nfDescTpl: nullIfBlank(s.projFatTpl_nfDescTpl),
          projFatTpl_epPrefix: nullIfBlank(epPrefix),
          projFatTpl_cnpjTom: String(s.projFatTpl_cnpjTom || '').replace(/\D/g, '') || null,
          mun_id: s.mun_id || null,
          projFatTpl_padrao: s.projFatTpl_padrao ? 1 : 0,
          projFatTpl_ord: i + 1,
          projFatTpl_ativo: 1,
        }
        if (s.projFatTpl_id) {
          const up = await oikosApi.post('/', {
            action: 'update',
            table: 'projFatTpl',
            data: { ...payload, projFatTpl_id: s.projFatTpl_id },
          })
          if (up?.status && up.status >= 400) throw new Error('projFatTpl')
          kept.add(Number(s.projFatTpl_id))
          idByIdx[i] = Number(s.projFatTpl_id)
        } else {
          const ins = await oikosApi.post('/', {
            action: 'insert',
            table: 'projFatTpl',
            data: payload,
          })
          if (ins?.status && ins.status >= 400) throw new Error('projFatTpl')
          const body = ins?.data
          const row = Array.isArray(body) ? body[0] : body
          let newId = Number(row?.projFatTpl_id) || Number(body?.insertId) || 0
          if (!(newId > 0)) {
            const again = await getRegs('projFatTpl', { proj_id: projId, projFatTpl_ativo: 1 })
            const hit = (again || []).find((rowTpl) => Number(rowTpl.mun_id) === Number(s.mun_id)
              && String(rowTpl.projFatTpl_nome) === nome)
            newId = Number(hit?.projFatTpl_id) || 0
          }
          if (newId > 0) {
            kept.add(newId)
            idByIdx[i] = newId
          }
        }
      }
      for (const id of origSeriesIds) {
        if (kept.has(Number(id))) continue
        const del = await oikosApi.post('/', {
          action: 'update',
          table: 'projFatTpl',
          data: { projFatTpl_id: id, projFatTpl_ativo: 0 },
        })
        if (del?.status && del.status >= 400) throw new Error('projFatTpl')
      }

      const recNext = {}
      series.forEach((s, idxSerie) => {
        const tid = idByIdx[idxSerie]
        if (!(tid > 0)) return
        (s.recIds || []).forEach((rid) => { recNext[Number(rid)] = tid })
      })
      const recIdSet = new Set([
        ...Object.keys(origRecTpl).map(Number),
        ...Object.keys(recNext).map(Number),
      ])
      for (const rid of recIdSet) {
        if (!(rid > 0)) continue
        const prev = origRecTpl[rid] || origRecTpl[String(rid)] || null
        const nextTpl = recNext[rid] || null
        if (Number(prev || 0) === Number(nextTpl || 0)) continue
        const serie = series.find((s, idxSerie) => idByIdx[idxSerie] === nextTpl)
        const patchRec = { rec_id: rid, projFatTpl_id: nextTpl }
        if (nextTpl && serie?.mun_id) patchRec.mun_id = serie.mun_id
        const recRes = await oikosApi.post('/', { action: 'update', table: 'rec', data: patchRec })
        if (recRes?.status && recRes.status >= 400) throw new Error('rec')
      }

      invalidateFatSupportCache()
      notifySuccess('Processo de faturamento salvo.')
      onClose()
    } catch (e) {
      console.log('ModalProcessoFaturamento save', e?.response?.data || e)
      const apiMsg = e?.response?.data?.message || e?.response?.data?.error || ''
      notifyError(`Erro ao salvar o processo de faturamento${apiMsg ? `: ${apiMsg}` : '.'}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving || uploading ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Processo de faturamento</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography variant="body2">Carregando...</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Processo</Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Como o projeto é faturado"
                value={form.proj_fatProc}
                onChange={(e) => setField('proj_fatProc', e.target.value)}
                helperText="Contrato do que cada receita precisa trazer em rec_desc. Se o cliente gera uma nova ordem de compra (PO) ou ITM por nota, escreva isso aqui — ex.: «Nova PO por nota; rec_desc precisa trazer PO, ITM e competência (Mês N. Mês/AAAA.).». O dashboard preenche as {chaves} a partir da descrição da receita e bloqueia a emissão se faltar; não inventa número."
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Pessoas</Typography>
              <Autocomplete
                size="small"
                options={respOptions}
                value={respValue}
                onChange={(e, v) => setField('proj_fatRespStake_id', v ? Number(v.projStake_id) : null)}
                getOptionLabel={(o) => stakeLabel(o)}
                isOptionEqualToValue={(a, b) => Number(a?.projStake_id) === Number(b?.projStake_id)}
                renderInput={(params) => (
                  <TextField {...params} label="Responsável no cliente" placeholder="Stakeholder do contratante" />
                )}
              />
              {contratanteId != null && !stakes.some((s) => Number(s.pj_id) === Number(contratanteId)) && stakes.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Nenhum stakeholder vinculado ao contratante. A lista mostra todos os stakeholders do projeto.
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>Quem recebe o e-mail da nota</Typography>
              {stakes.length === 0 && (
                <Typography variant="caption" color="text.secondary">Este projeto não tem stakeholders.</Typography>
              )}
              {stakes.map((s) => (
                <FormControlLabel
                  key={s.projStake_id}
                  control={(
                    <Checkbox
                      size="small"
                      checked={form.recebeIds.includes(Number(s.projStake_id))}
                      onChange={() => toggleId('recebeIds', Number(s.projStake_id))}
                    />
                  )}
                  label={stakeLabel(s)}
                />
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Outros destinatários"
                  placeholder="grace.l@example.com"
                  value={form.emailDraft}
                  onChange={(e) => setField('emailDraft', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addEmail()
                    }
                  }}
                />
                <Button variant="outlined" onClick={addEmail} sx={{ mt: 0.2 }}>Adicionar</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {form.emails.map((em) => (
                  <Chip
                    key={em}
                    size="small"
                    label={em}
                    onDelete={() => setForm((f) => ({ ...f, emails: f.emails.filter((x) => x !== em) }))}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Prazos</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  type="number"
                  label="Dia limite no mês"
                  value={form.proj_fatDiaLim}
                  onChange={(e) => setField('proj_fatDiaLim', e.target.value)}
                  inputProps={{ min: 1, max: 31 }}
                  helperText="1 a 31. Em meses curtos, vale o último dia."
                  sx={{ width: 220 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Prazo de pagamento (dias)"
                  value={form.contrato_prazoPgto}
                  onChange={(e) => setField('contrato_prazoPgto', e.target.value)}
                  disabled={!contratoId}
                  helperText={contratoId ? 'Gravado no contrato' : 'Projeto sem contrato'}
                  inputProps={{ min: 0, max: 3650 }}
                  sx={{ width: 240 }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Nota fiscal</Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Template da descrição"
                value={form.proj_nfDescTpl}
                onChange={(e) => setField('proj_nfDescTpl', e.target.value)}
                helperText="Vale quando o projeto tem uma serie so. Com N series (filial/cidade/UHE), o texto de cada serie abaixo e o que vai na nota."
                sx={{ mb: 1.5 }}
              />
                            <SeriesFaturamentoEditor
                series={series}
                setSeries={setSeries}
                muns={muns}
                recs={recs}
                defaultTpl={form.proj_nfDescTpl}
              />

<Autocomplete
                size="small"
                options={tplOptions}
                value={tplValue}
                onChange={(e, v) => setField('retTemplate_id', v ? Number(v.retTemplate_id) : null)}
                getOptionLabel={(o) => o?.retTemplate_desc || ''}
                isOptionEqualToValue={(a, b) => Number(a?.retTemplate_id) === Number(b?.retTemplate_id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Template de retenções"
                    helperText="Tabelas de imposto cadastradas na Kamino."
                  />
                )}
                sx={{ mb: 1.5 }}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Código tributário"
                  value={form.contrato_codTrib}
                  onChange={(e) => setField('contrato_codTrib', e.target.value)}
                  disabled={!contratoId}
                  sx={{ width: 220 }}
                />
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 280, flex: 1 }}
                  disabled={!contratoId}
                  options={muns}
                  value={munValue}
                  onChange={(e, v) => setField('contrato_munISS', v ? Number(v.mun_id) : null)}
                  getOptionLabel={(o) => (o?.mun_nome ? `${o.mun_nome}${o.uf_IBGECod ? ` (${o.uf_IBGECod})` : ''}` : '')}
                  isOptionEqualToValue={(a, b) => Number(a?.mun_id) === Number(b?.mun_id)}
                  renderInput={(params) => <TextField {...params} label="Município de incidência do ISS" />}
                />
                <TextField
                  size="small"
                  label="CNO"
                  value={form.proj_cno}
                  onChange={(e) => setField('proj_cno', e.target.value.replace(/\D/g, '').slice(0, 12))}
                  helperText="Código da obra, até 12 dígitos"
                  sx={{ width: 220 }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>E-mail da nota</Typography>
              <TextField
                fullWidth
                size="small"
                label="Assunto"
                value={form.proj_fatEmailAssunto}
                onChange={(e) => setField('proj_fatEmailAssunto', e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                size="small"
                label="Corpo do e-mail"
                value={form.proj_fatEmailTpl}
                onChange={(e) => setField('proj_fatEmailTpl', e.target.value)}
                helperText="A nota fiscal segue em anexo no envio."
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Documentação de suporte</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                CNDs, folha, guias e comprovantes da Ecossis, mais arquivos deste projeto. O que estiver marcado vai no e-mail da NFS-e junto com o XML e o PDF.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {FAT_SUPPORT_DOC_TYPES.filter((t) => t.id !== 18).map((t) => (
                  <Chip
                    key={t.id}
                    size="small"
                    variant={selectedTypeIds.has(t.id) ? 'filled' : 'outlined'}
                    color={selectedTypeIds.has(t.id) ? 'success' : 'default'}
                    label={t.label}
                  />
                ))}
              </Box>
              <Autocomplete
                multiple
                size="small"
                options={docOptions}
                groupBy={(o) => o.group || ''}
                value={selectedDocs}
                onChange={(e, v) => setField('fatDocIds', (v || []).map((d) => Number(d.documento_id)))}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) => Number(a?.documento_id) === Number(b?.documento_id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Documentos que acompanham a NFS-e"
                    helperText={docOptions.length
                      ? 'Escolha comprovantes da Ecossis ou arquivos já ligados a este projeto.'
                      : 'Nenhum documento institucional ou do projeto ainda. Anexe um arquivo abaixo.'}
                  />
                )}
                sx={{ mb: 1.5 }}
              />
              <Typography variant="body2" sx={{ mb: 1 }}>Anexar arquivo deste projeto</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 220, flex: 1 }}
                  options={FAT_SUPPORT_DOC_TYPES}
                  value={uploadTp}
                  onChange={(e, v) => setUploadTp(v)}
                  getOptionLabel={(o) => o?.label || ''}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  renderInput={(params) => <TextField {...params} label="Tipo" />}
                />
                <TextField
                  size="small"
                  sx={{ minWidth: 200, flex: 1 }}
                  label="Descrição (opcional)"
                  value={uploadDesc}
                  onChange={(e) => setUploadDesc(e.target.value)}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Emissão"
                  InputLabelProps={{ shrink: true }}
                  value={uploadDtEmissao}
                  onChange={(e) => setUploadDtEmissao(e.target.value)}
                  sx={{ width: 170 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Válido até"
                  InputLabelProps={{ shrink: true }}
                  value={uploadFimVig}
                  onChange={(e) => setUploadFimVig(e.target.value)}
                  required
                  sx={{ width: 170 }}
                />
                <input
                  ref={fileRef}
                  type="file"
                  hidden
                  accept=".pdf,.png,.jpg,.jpeg,.xml,.zip"
                  onChange={onPickFile}
                />
                <Button
                  variant="outlined"
                  disabled={uploading || !uploadTp || !projId}
                  onClick={() => fileRef.current && fileRef.current.click()}
                  startIcon={uploading ? <CircularProgress size={16} /> : null}
                  sx={{ mt: 0.2 }}
                >
                  {uploading ? 'Enviando…' : 'Escolher arquivo'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                O arquivo fica nos documentos do projeto e entra na seleção acima. Salve o processo para ele ir no próximo e-mail da nota.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || uploading}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={loading || saving || uploading || !projId}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
